<template>
  <div class="organization-edit">
    <div class="panel-topnav">
      <router-link
        v-show="user.isAdmin"
        :to="{ name: 'adminOrganizationList' }"
        class="back-link pull-left"
      >
        <i class="ion-ios-arrow-back text-yellow before" /> Organizations
      </router-link>
      <ul class="nav nav-tabs">
        <li><h3 class="page-title">My organization</h3></li>
      </ul>
      <div class="clearfix" />
    </div>

    <div class="panel-card">
      <div
        class="panel-card-content"
        :class="{ 'loader-cover': fetchInProgress }"
      >
        <h5>Organization: {{ original.name }}</h5>
        <fieldset>
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="control-label">Name</label>
                <input v-model="name" class="form-control input-sm" />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group page-link-field">
                <label class="control-label">Application page visibility</label>
                <div class="checkbox">
                  <label>
                    <input v-model="isPublic" type="checkbox" />
                    <span>Public</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group page-link-field">
                <label class="control-label">Application page URL</label>
                <p class="hint text">
                  Your application page would be available at:
                </p>
                <router-link
                  v-if="organizationFetched"
                  class="btn btn-xs btn-transparent btn-secondary"
                  :to="organizationRoute"
                  target="_blank"
                >
                  Application Link
                  <i class="ion-android-open after" />
                </router-link>
              </div>
            </div>
          </div>

          <div v-if="user.isAdmin" class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="control-label">Investor</label>
                <p>
                  <router-link
                    v-if="organization.investor"
                    :to="{
                      name: 'adminInvestorEditor',
                      params: { id: organization.investor.id },
                    }"
                    >{{ organization.investor.name }}</router-link
                  >
                  <span v-else>
                    No investor
                  </span>
                  <a href @click.prevent="$refs.pickInvestorDialog.open()"
                    >(click here to
                    <span v-if="organization.investor">change</span>
                    <span v-else>set</span>)
                  </a>
                </p>
                <PickInvestorDialog
                  ref="pickInvestorDialog"
                  :organization-id="organization.id"
                  @picked="onInvestorChange"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="control-label">Logo</label>
                <p v-show="!logoUrl" class="text-warning">
                  No logo uploaded yet
                </p>
                <img class="img-responsive" :src="logoUrl" v-show="logoUrl" />
                <dropzone
                  class="margin-top-10"
                  title="Drop logo here"
                  @upload-done="onUploadDone($event)"
                />
                <p class="hint">
                  Best if logo is <span class="bold">600 pixels wide</span> and
                  has
                  <span class="bold">height between 300 and 600 pixels</span>.
                </p>
              </div>
              <a href class="btn btn-primary" @click.prevent="save()">
                Save changes
              </a>
            </div>
          </div>
        </fieldset>

        <OrganizationOwnerList class="margin-top-30" />
      </div>
    </div>

    <div class="panel-card" v-if="user.isAdmin">
      <div
        class="panel-card-content"
        :class="{ 'loader-cover': fetchInProgress }"
      >
        <h5>Admin only</h5>

        <el-form ref="form" label-width="200px">
          <el-form-item label="Demo mode">
            <el-switch v-model="hasDemoModeEnabled"></el-switch>
          </el-form-item>

          <el-form-item label="Demo mode for reports">
            <el-switch v-model="hasDemoModeEnabledReports"></el-switch>
          </el-form-item>

          <el-form-item label="Type">
            <el-radio v-model="type" :label="null">None</el-radio>
            <el-radio v-model="type" label="VC">VC</el-radio>
            <el-radio v-model="type" label="CORP">Corp</el-radio>
          </el-form-item>
        </el-form>

        <el-button
          :disabled="!formHasPendingChanges"
          type="primary"
          @click="save()"
          >Save</el-button
        >

        <div class="">
          <AdminOrganizationRegistrationsList
            :organization-id="realOrgId"
            :enabled="registrationListEnabled"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import notifications from 'services/notifications'
import _ from 'lodash'
import organizations from 'services/organizations'

import OrganizationOwnerList from './OrganizationOwnerList.vue'
import PickInvestorDialog from 'components/organizations/PickInvestorDialog.vue'
import AdminOrganizationRegistrationsList from '@/modules/admin/AdminOrganizationRegistrationsList.vue'
import { updateTitle } from 'modules/admin/routes'

export default {
  components: {
    AdminOrganizationRegistrationsList,
    PickInvestorDialog,
    OrganizationOwnerList,
  },
  data: () => {
    return {
      errors: getDefaultErrors(),
      realOrgId: '',
      registrationListEnabled: false,
      formHasPendingChanges: false,
    }
  },
  beforeMount() {
    this.realOrgId = this.$route.params.organizationId
  },
  async created() {
    let orgId
    if (this.$route.name === 'adminOrganizationEdit') {
      const id = this.$route.params.organizationId
      this.$store.dispatch('organizationDetails/fetch', { id }).then(() => {
        updateTitle(`Organization: ${this.name}`)
      })
      orgId = id
    } else {
      const user = await this.$store.dispatch('user/getUser')
      await this.$store
        .dispatch('organizationDetails/fetch', {
          id: user.organizationId,
        })
        .then(() => {
          updateTitle(`Organization: ${this.name}`)
        })
      orgId = user.organizationId
    }
    this.realOrgId = (await organizations.idToName(orgId)) || orgId
    this.registrationListEnabled = true
    window.vm = this
  },
  beforeDestroy() {
    this.$store.commit('organizationDetails/CLEAR')
  },
  computed: {
    name: {
      get() {
        return this.$store.state.organizationDetails.organization.name
      },
      set: _.debounce(function (value) {
        this.$store.commit('organizationDetails/UPDATE_NAME', { name: value })
      }, 250),
    },
    logoUrl: {
      get() {
        return this.$store.state.organizationDetails.organization.logo_url
      },
      set(value) {
        this.$store.commit('organizationDetails/UPDATE_LOGO_URL', {
          logo_url: value,
        })
      },
    },
    isPublic: {
      get() {
        return this.$store.state.organizationDetails.organization.is_public
      },
      set(value) {
        this.$store.commit('organizationDetails/UPDATE_IS_PUBLIC', {
          is_public: value,
        })
      },
    },
    type: {
      get() {
        return this.$store.state.organizationDetails.organization.type
      },
      set(value) {
        this.$store.commit('organizationDetails/UPDATE_TYPE', { type: value })
        this.formHasPendingChanges = true
      },
    },
    hasDemoModeEnabled: {
      get() {
        return this.$store.state.organizationDetails.organization
          .has_demo_mode_enabled
      },
      set(value) {
        this.$store.commit('organizationDetails/UPDATE_HAS_DEMO_MODE_ENABLED', {
          hasDemoModeEnabled: value,
        })
        this.formHasPendingChanges = true
      },
    },
    hasDemoModeEnabledReports: {
      get() {
        return this.$store.state.organizationDetails.organization
          .has_demo_mode_enabled_reports
      },
      set(value) {
        this.$store.commit(
          'organizationDetails/UPDATE_HAS_DEMO_MODE_ENABLED_REPORTS',
          {
            hasDemoModeEnabledReports: value,
          },
        )
        this.formHasPendingChanges = true
      },
    },
    ...mapState({
      user: (state) => state.user.data,
      original: (state) => state.organizationDetails.original,
      organization: (state) => state.organizationDetails.organization,
      organizationFetched: (state) => state.organizationDetails.fetched,
      owners: (state) => state.adminOwnerList.owners,
      fetchInProgress: (state) => state.organizationDetails.fetchInProgress,
      organizationRoute(state) {
        const orgId = this.realOrgId
        return { name: 'organizationPage', params: { orgId } }
      },
    }),
  },
  methods: {
    async save({ successAction = 'Organization saved' } = {}) {
      try {
        await this.$store.dispatch('organizationDetails/save', {
          name: this.name,
          logo_url: this.logoUrl,
          is_public: this.isPublic,
          has_demo_mode_enabled: this.hasDemoModeEnabled,
          has_demo_mode_enabled_reports: this.hasDemoModeEnabledReports,
          type: this.type,
        })
      } catch (error) {
        console.error(' => ERROR: ', error)
        this.errors = error.response.data
        this.$message({ type: 'danger', message: 'Something went wrong' })
        return
      }
      this.errors = getDefaultErrors()
      this.$message({ type: 'success', message: successAction })
      this.formHasPendingChanges = false
    },
    onUploadDone(event) {
      this.logoUrl = event.data.url
    },
    onInvestorChange({ investor }) {
      this.$store.dispatch('organizationDetails/fetch', { id: this.realOrgId })
    },
    async toggleFeatureFunnel(enabled) {
      const feature = 'funnel'
      const action = enabled ? 'enableFeature' : 'disableFeature'
      const msgAction = enabled ? 'enabled' : 'disabled'
      this.$message({
        type: 'success',
        message: `Feature ${msgAction} successfully`,
      })
      this.$store.dispatch(`organizationDetails/${action}`, { feature })
    },
  },
}

function getDefaultErrors() {
  return {
    name: [],
    logo_url: [],
  }
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.organization-edit {
  .page-link-field {
    .checkbox {
      margin-bottom: 0;
      padding-bottom: 0;
    }
    .hint {
      font-size: 14px;
      font-weight: 300;
    }
  }

  .hint {
    padding-top: 5px;
    color: $grey120;
    font-size: 14px;
    font-weight: 300;

    .bold {
      font-weight: 700;
    }
  }
}
</style>
